// src/components/home/HomeProductSection.js
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/home/HomeProductSection.module.css';
import { fetchHomeAIUrl, fetchGPTCarIds } from '../../api/orderApi';
import LoginModal from '../LoginModal';
import AlertModal from '../AlertModal';
import RechargeModal from '../RechargeModal';
import AlertImageModal from '../AlertImageModal'; 

const formatExpiryDate = (dateString) => {
  if (!dateString) return '未设置';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const expiryDateMap = {
  gpt: 'expiration_date',  
  sora: 'expiration_date',
  claude: 'expiration_date_ye_claude',
  mj: 'expiration_date_ye_mj',
  grok: 'expiration_date_ye_grok',
  gpt_api: 'expiration_date_ye_chatgpt',
  gemini:'expiration_date_ye_chatgpt',
  deepseek:'expiration_date'
};

const HomeProductSection = ({ id }) => {
  // 使用 i18n 的翻译钩子
  const { t } = useTranslation();

  // 产品数据使用翻译键
  const homeProducts = [
    { 
      id: 'gpt', 
      name: t('homeProducts.gpt.name'), 
      image: `${process.env.PUBLIC_URL}/images/gpt-main.jpg`, 
      link: '/fleet',
      quota: t('homeProducts.gpt.quota')
    },
    { 
      id: 'sora', 
      name: t('homeProducts.sora.name'), 
      image: `${process.env.PUBLIC_URL}/images/sora-main.jpg`, 
      link: '/fleet',
      quota: t('homeProducts.sora.quota')
    },
    { 
      id: 'grok', 
      name: t('homeProducts.grok.name'), 
      image: `${process.env.PUBLIC_URL}/images/grok-main.jpg`, 
      link: '/fleet',
      quota: t('homeProducts.grok.quota')
    },
    { 
      id: 'claude', 
      name: t('homeProducts.claude.name'), 
      image: `${process.env.PUBLIC_URL}/images/claude-main.jpg`, 
      link: '/fleet',
      quota: t('homeProducts.claude.quota')
    },
  ];

  // 获取用户额度的函数 (移到组件内部，这样可以访问 t 和 homeProducts)
  const getUserQuota = (productId, user) => {
    if (!user) return null;
    // 这里应该是从用户信息中获取具体额度的逻辑
    // 目前直接返回产品的国际化配额文本
    const product = homeProducts.find(p => p.id === productId);
    return product ? product.quota : t('homeProducts.messages.unknownError');
  };

  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState({});
  const [expiredStatus, setExpiredStatus] = useState({});
  const [successUrl, setSuccessUrl] = useState(null);
  const [isDeepSeekModalOpen, setIsDeepSeekModalOpen] = useState(false);
  const [isGeminiModalOpen, setIsGeminiModalOpen] = useState(false);

  useEffect(() => {
    console.log('useEffect triggered, user:', user, 'isLoggedIn:', isLoggedIn);
    const updateExpiredStatus = () => {
      if (isLoggedIn && user) {
        const newExpiredStatus = {};
        homeProducts.forEach((product) => {
          const field = expiryDateMap[product.id];
          const expiryDate = user[field];
          if (expiryDate) {
            const expirationDateUTC = new Date(expiryDate);
            const nowUTC = new Date();
            newExpiredStatus[product.id] = nowUTC > expirationDateUTC;
            console.log(`${product.id} - Expiry Date (UTC):`, expiryDate);
            console.log(`${product.id} - Now (UTC):`, nowUTC.toISOString());
            console.log(`${product.id} - Is Expired:`, newExpiredStatus[product.id]);
          } else {
            newExpiredStatus[product.id] = false;
            console.log(`${product.id} - No expiry date found, not expired`);
          }
        });
        setExpiredStatus(newExpiredStatus);
        console.log('Updated expiredStatus:', newExpiredStatus);
      } else {
        console.log('User not logged in or no user data');
      }
    };

    updateExpiredStatus();
    const interval = setInterval(updateExpiredStatus, 60000);
    return () => clearInterval(interval);
  }, [user, isLoggedIn, homeProducts]);

  const handleUseNowClick = (productId) => {
    console.log(`Button clicked for ${productId}, expiredStatus:`, expiredStatus[productId]);

    if (expiredStatus[productId]) {
      console.log(`${productId} is expired, opening RechargeModal`);
      setSelectedProductId(productId);
      setIsRechargeModalOpen(true);
      return;
    }

    if (isLoading[productId]) {
      console.log(`${productId} is loading, ignoring click`);
      return;
    }

    if (productId === 'deepseek') {
      console.log('Deep Seek clicked, opening AlertImageModal');
      setIsDeepSeekModalOpen(true);
      return;
    }

    if (productId === 'gemini') {
      console.log('Gemini 2.5 clicked, opening AlertImageModal');
      setIsGeminiModalOpen(true);
      return;
    }

    const usertoken = user?.userToken || localStorage.getItem('userToken');
    const checkToken = user?.checkToken || localStorage.getItem('checkToken');
    const host = window.location.hostname;

    if (!usertoken || !isLoggedIn) {
      console.log('User not logged in or no usertoken, opening LoginModal');
      setIsLoginModalOpen(true);
      return;
    }

    if (!host) {
      console.log('No host found');
      setAlertMessage(t('homeProducts.messages.noHost'));
      setIsAlertModalOpen(true);
      return;
    }

    setSuccessUrl(null);
    setAlertMessage(t('homeProducts.messages.loading'));
    setIsAlertModalOpen(true);
    setIsLoading((prev) => ({ ...prev, [productId]: true }));

    (async () => {
      try {
        if (productId === 'gpt') {
          const requestData = { host, usertoken, checkToken };
          const response = await fetchGPTCarIds(requestData);
          console.log('fetchGPTCarIds response:', response);
          if (response.message === 'Success' && response.url) {
            setSuccessUrl(response.url);
            window.open(response.url, '_blank');
            setAlertMessage(t('homeProducts.messages.redirectSuccess'));
          } else {
            setAlertMessage(t('homeProducts.messages.requestFailed', { error: response.data?.message || t('homeProducts.messages.unknownError') }));
          }
        } else {
          const user_key = user?.user_key;
          if (!user_key) {
            console.log('No user_key, opening LoginModal');
            setIsLoginModalOpen(true);
            setIsAlertModalOpen(false);
            return;
          }
          const requestData = { user_key, type: productId, host };
          const response = await fetchHomeAIUrl(requestData);
          console.log('fetchHomeAIUrl response:', response);
          if (response.message === 'Success' && response.data.url) {
            setSuccessUrl(response.data.url);
            window.open(response.data.url, '_blank');
            setAlertMessage(t('homeProducts.messages.redirectSuccess'));
          } else {
            setAlertMessage(t('homeProducts.messages.requestFailed', { error: response.data?.message || t('homeProducts.messages.unknownError') }));
          }
        }
      } catch (error) {
        console.error(`Error for ${productId}:`, error.message);
        setAlertMessage(t('homeProducts.messages.requestFailed', { error: error.message }));
      } finally {
        setIsLoading((prev) => ({ ...prev, [productId]: false }));
      }
    })();
  };

  const handleCloseModal = () => setIsLoginModalOpen(false);
  const handleLoginSuccess = () => setIsLoginModalOpen(false);
  const handleAlertClose = () => setIsAlertModalOpen(false);
  const handleRechargeClose = () => {
    console.log('Closing RechargeModal');
    setIsRechargeModalOpen(false);
    setSelectedProductId(null);
  };

  const handleDeepSeekModalClose = () => {
    console.log('Closing AlertImageModal');
    setIsDeepSeekModalOpen(false);
  };

  const handleGeminiModalClose = () => {
    console.log('Closing Gemini AlertImageModal');
    setIsGeminiModalOpen(false);
  };

  const getExpiryDate = (productId) => {
    if (!isLoggedIn || !user) return null;
    const field = expiryDateMap[productId];
    return user[field] || null;
  };

  return (
    <section id={id} className={styles.homeProductSection}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>{t('homeProducts.sectionTitle')}</h2>
        <div className={styles.productGrid}>
          {homeProducts.map((product) => {
            const expiryDate = getExpiryDate(product.id);
            const isExpired = expiredStatus[product.id];
            const quota = getUserQuota(product.id, user);
            
            return (
              <div key={product.id} className={styles.productCard}>
                <div className={styles.imageContainer}>
                  <img src={product.image} alt={t('homeProducts.imageAlt', { name: product.name })} className={styles.mainImage} />
                </div>
                <h3>{product.name}</h3>
                
                <div className={styles.infoContainer}>
                  {isLoggedIn && expiryDate && (
                    <div className={styles.expiryDateContainer}>
                      <span className={styles.expiryLabel}>{t('homeProducts.expiryTime')}</span>
                      <span className={styles.expiryDate}>{formatExpiryDate(expiryDate)}</span>
                    </div>
                  )}
                  
                  {/* 额度说明 */}
                  <div className={styles.quotaContainer}>
                    <span className={styles.quotaLabel}>{t('homeProducts.usageQuota')}</span>
                    <span className={styles.quotaValue}>{quota || product.quota}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => handleUseNowClick(product.id)}
                  className={styles.btn}
                >
                  {isLoading[product.id] ? t('homeProducts.buttons.redirecting') : 
                   isExpired ? t('homeProducts.buttons.expiredRenew') : 
                   t('homeProducts.buttons.useNow')}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseModal} onLoginSuccess={handleLoginSuccess} />
      <AlertModal
        isOpen={isAlertModalOpen}
        message={alertMessage}
        onClose={handleAlertClose}
        onConfirm={successUrl ? () => window.location.href = successUrl : null}
        confirmText={successUrl ? t('homeProducts.buttons.manualRedirect') : t('homeProducts.buttons.confirm')}
      />
      <RechargeModal
        isOpen={isRechargeModalOpen}
        onClose={handleRechargeClose}
        currentTab={selectedProductId}
        userEmail={user?.email || localStorage.getItem('email')}
      />

      <AlertImageModal
        isOpen={isDeepSeekModalOpen}
        message={
          <>
            <p>{t('homeProducts.deepseek.message1')}</p>
            <p>{t('homeProducts.deepseek.message2')}</p>
          </>
        }
        imageUrl={`${process.env.PUBLIC_URL}/images/deepseek-teacher.jpg`}
        onClose={handleDeepSeekModalClose}
        onConfirm={handleDeepSeekModalClose}
        confirmText={t('homeProducts.buttons.confirm')}
      />

      <AlertImageModal
        isOpen={isGeminiModalOpen}
        message={
          <>
            <p>{t('homeProducts.gemini.message1')}</p>
            <p>{t('homeProducts.gemini.message2')}</p>
          </>
        }
        imageUrl={`${process.env.PUBLIC_URL}/images/gemini-teacher.jpg`}
        onClose={handleGeminiModalClose}
        onConfirm={handleGeminiModalClose}
        confirmText={t('homeProducts.buttons.confirm')}
      />
    </section>
  );
};

export default React.memo(HomeProductSection);